import type { ColorMatchMimeType } from "@color-finder/color-finder-api/models";
import { getAuthenticatedAxios } from "hooks/authentication";
import type { ExtractResponse, Region } from "./types";

interface ExtractRequest {
  image: string;
  mimeType: ColorMatchMimeType;
  regions: { x: number; y: number; width: number; height: number }[];
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function convertToJpegBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas context unavailable"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        resolve(dataUrl.split(",")[1]);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image for conversion"));
    };
    img.src = objectUrl;
  });
}

function regionsToPixels(
  regions: Region[],
  imageWidth: number,
  imageHeight: number,
): ExtractRequest["regions"] {
  return regions.map((r) => ({
    x: Math.round((r.x / 100) * imageWidth),
    y: Math.round((r.y / 100) * imageHeight),
    width: Math.round((r.width / 100) * imageWidth),
    height: Math.round((r.height / 100) * imageHeight),
  }));
}

/**
 * Calls the `POST /v0/color-match/extract` endpoint to extract a color
 * palette from each selected region of the uploaded image.
 *
 * The endpoint expects pixel-space region rectangles on a PNG/JPEG body;
 * this function handles both conversions:
 * - Images already typed `image/png` or `image/jpeg` are base64-encoded as-is.
 * - All other types (WEBP, etc.) are decoded into a canvas and re-encoded
 *   as JPEG at quality 0.92 before upload.
 * - Region coordinates are stored as **percentages** (0–100) in `Region`;
 *   they are multiplied by `imageNaturalSize` and rounded to integer pixels
 *   here before being sent.
 *
 * @param file The user-uploaded image. Its `type` drives the MIME branch.
 * @param regions Regions in percentage units (see {@link Region}).
 * @param imageNaturalSize Natural pixel dimensions of the loaded image,
 *   used to convert percentage regions to pixel regions for the request.
 * @returns The decoded {@link ExtractResponse} with one palette per region.
 * @throws Propagates axios errors (non-2xx, network, auth) and "Canvas
 *   context unavailable" / "Failed to load image for conversion" from the
 *   WEBP re-encode path.
 */
export async function extractColorPalettes(
  file: File,
  regions: Region[],
  imageNaturalSize: { width: number; height: number },
): Promise<ExtractResponse> {
  const { axios } = getAuthenticatedAxios();

  let base64: string;
  let mimeType: ColorMatchMimeType;

  if (file.type === "image/png") {
    base64 = await fileToBase64(file);
    mimeType = "image/png";
  } else if (file.type === "image/jpeg") {
    base64 = await fileToBase64(file);
    mimeType = "image/jpeg";
  } else {
    base64 = await convertToJpegBase64(file);
    mimeType = "image/jpeg";
  }

  const pixelRegions = regionsToPixels(
    regions,
    imageNaturalSize.width,
    imageNaturalSize.height,
  );

  const body: ExtractRequest = {
    image: base64,
    mimeType,
    regions: pixelRegions,
  };

  const response = await axios.post<ExtractResponse>(
    "/v0/color-match/extract",
    body,
  );
  return response.data;
}
